import React from 'react';
import Link from 'next/link';
import { GlobalImage, ImageEntity, ImageType } from 'src/modules/global';
import { Col, Card } from 'antd';
import { ColProps } from 'antd/lib/col';
import classnames from 'classnames';
import { colSmallCardProps, colMediumCardProps, colLargeCardProps } from 'src/utils/grid';
import styles from './LayoutCard.module.scss';
import { getImageBreakpointUrl } from 'src/utils/image';
import { withBreakpoints } from 'react-breakpoints';

export enum LayoutCardSize {
  Big = 'Big',
  Small = 'Small',
}

export enum LayoutCardType {
  Product = 'Product',
  Overview = 'Overview',
  Article = 'Article',
  Informative = 'Informative',
  Instagram = 'Instagram',
}

export interface LayoutCardData {
  // id: string;
  title?: string;
  slug?: string;
  description?: string;
  image: ImageEntity;
  caption?: string;
  link?: string;
  as?: string;
  href?: string;
  footer?: string;
  subtitle?: string;
  count?: number;
  colProps?: ColProps;
  onClick?: () => void;
}

export interface LayoutCardProps {
  data: LayoutCardData;
  bordered?: boolean;
  hoverable?: boolean;
  size?: LayoutCardSize; // Default = Small
  type?: LayoutCardType; // Default = Overview
  colProps?: ColProps;
  dynamicRoute?: boolean;
  children?: React.ReactNode;
  truncateTitle?: boolean;
  objectFit?: 'cover' | 'contain';
  as?: string;
  currentBreakpoint: string;
}

const LayoutCard: React.FunctionComponent<LayoutCardProps> = (props: LayoutCardProps) => {
  // const { Meta } = Card;
  const {
    children,
    data: {
      href,
      as,
      link,
      slug,
      image,
      title,
      caption,
      footer,
      subtitle,
      count,
      description,
      onClick,
    },
    size = LayoutCardSize.Small,
    type = LayoutCardType.Overview,
    hoverable = false,
    dynamicRoute = true,
    colProps: initialColProps,
    truncateTitle = false,
    objectFit = 'cover',
    currentBreakpoint,
    ...restProps
  } = props;

  let padding = false;
  let colprops, imageClass;

  // switch (size) {
  //   case LayoutCardSize.Big:
  //     colprops = colLargeCardProps;
  //     break;
  //   case LayoutCardSize.Small:
  //   default:
  //     colprops = colSmallCardProps;
  // }

  let cardTypeClass = '';
  let ratio: number;
  switch (type) {
    case LayoutCardType.Instagram:
      ratio = 1;
      padding = false;
      cardTypeClass = styles.instagram;
      break;
    case LayoutCardType.Product:
      if (size === LayoutCardSize.Big) {
        // imageClass = styles.imageExtraLarge;
        colprops = colMediumCardProps;
      } else {
        //@TODO check if the image is horizontal or vertical
        //Horizontal
        // colprops = colLargeCardProps;
        // imageClass = styles.imageMedium;
        colprops = colSmallCardProps;
        ratio = 1;
      }
      padding = true;
      break;
    case LayoutCardType.Overview:
      colprops = colSmallCardProps;
      cardTypeClass = styles.overview;
      if (size === LayoutCardSize.Big) {
        colprops = colLargeCardProps;
      }
      ratio = 3 / 2;

      break;
    case LayoutCardType.Article:
      if (size === LayoutCardSize.Big) {
        imageClass = styles.imageLarge;
        colprops = colLargeCardProps;
        ratio = 3 / 2;
      } else {
        imageClass = styles.imageSmall;
        colprops = colMediumCardProps;
      }
      break;

    default:
      imageClass = styles.imageSmall;
      colprops = colMediumCardProps;
  }

  // If colprops is passed as a prop, replace it
  colprops = initialColProps ? initialColProps : colprops;

  const responsiveImage = getImageBreakpointUrl(currentBreakpoint, image);
  const cardOutput = (
    <Card
      {...restProps}
      className={classnames(
        padding ? styles.cardPadding : styles.card,
        cardTypeClass,
        truncateTitle && styles.truncateTitle
      )}
      hoverable={hoverable}
      cover={
        <div className={imageClass}>
          <GlobalImage
            type={ImageType.Raw}
            alt={image?.alt}
            url={responsiveImage?.url || responsiveImage}
            ratio={ratio}
            objectFit={objectFit}
          />
        </div>
      }>
      <div className={styles.caption}>{caption}</div>
      {title && (
        <div className={styles.title}>
          <h4>{title}</h4>
          {count && <span className={styles.count}>{count}</span>}
        </div>
      )}
      {description && (
        <div className={styles.description} dangerouslySetInnerHTML={{ __html: description }} />
      )}
      {!!subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      {footer && <p className={styles.footer}>{footer}</p>}
      {children}
    </Card>
  );

  const usedLink = link ? `${link.startsWith('/') ? '' : '/'}${link}` : '';
  const usedSlug = slug ? `${slug.startsWith('/') ? '' : '/'}${slug}` : '';

  if (onClick) {
    return (
      <Col {...colprops} className={styles.container}>
        <a onClick={onClick}>{cardOutput}</a>
      </Col>
    );
  }

  return (
    <Col {...colprops} className={styles.container}>
      {usedSlug ? (
        <Link
          href={href || `${usedLink}${dynamicRoute ? '/[slug]' : usedSlug}`}
          as={as || `${usedLink}${usedSlug}`}>
          <a>{cardOutput}</a>
        </Link>
      ) : (
        cardOutput
      )}
    </Col>
  );
};

export default withBreakpoints(LayoutCard);
